import { useState } from "react";
import type { FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { useCollection } from "@/hooks/useCollection";
import ImageUpload from "@/pages/admin/components/ImageUpload";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

const iconOptions = [
  "ri-heart-3-line",
  "ri-restaurant-line",
  "ri-hand-heart-line",
  "ri-group-line",
  "ri-user-heart-line",
  "ri-shield-check-line",
  "ri-flashlight-line",
  "ri-map-pin-line",
  "ri-team-line",
  "ri-book-open-line",
  "ri-first-aid-kit-line",
  "ri-eye-line",
  "ri-file-list-3-line",
  "ri-moon-clear-line",
  "ri-flag-line",
  "ri-chat-smile-3-line",
];

export interface CrudField {
  key: string;
  label: string;
  type?: "text" | "textarea" | "number" | "icon" | "image";
  required?: boolean;
  placeholder?: string;
}

interface AdminCrudProps {
  title: string;
  description: string;
  addLabel: string;
  table: string;
  fields: CrudField[];
  listTitleKey: string;
  listSubtitleKey?: string;
  listIconKey?: string;
  listImageKey?: string;
}

type Row = Record<string, unknown> & { id: string };

function buildEmptyForm(fields: CrudField[]): Record<string, unknown> {
  const form: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.type === "number") form[field.key] = 0;
    else if (field.type === "icon") form[field.key] = "ri-heart-3-line";
    else form[field.key] = "";
  }
  return form;
}

export default function AdminCrud({
  title,
  description,
  addLabel,
  table,
  fields,
  listTitleKey,
  listSubtitleKey,
  listIconKey,
  listImageKey,
}: AdminCrudProps) {
  const { items, loading, error, reload } = useCollection<Row>(table);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [isError, setIsError] = useState(false);

  function startAdd() {
    setEditingId(null);
    const empty = buildEmptyForm(fields);
    empty.sort_order = items.length;
    setForm(empty);
    setShowForm(true);
    setMsg("");
  }

  function startEdit(item: Row) {
    setEditingId(item.id);
    const next: Record<string, unknown> = {};
    for (const field of fields) {
      next[field.key] = item[field.key] ?? "";
    }
    setForm(next);
    setShowForm(true);
    setMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function updateField(key: string, value: unknown) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setIsError(false);

    let err = null;
    if (editingId) {
      const res = await supabase.from(table).update(form).eq("id", editingId);
      err = res.error;
    } else {
      const res = await supabase.from(table).insert(form);
      err = res.error;
    }

    setSaving(false);
    if (err) {
      setMsg(err.message);
      setIsError(true);
      return;
    }

    setMsg(editingId ? "Élément modifié." : "Élément ajouté.");
    setShowForm(false);
    setEditingId(null);
    setForm({});
    reload();
  }

  async function handleDelete(id: string) {
    const { error: err } = await supabase.from(table).delete().eq("id", id);
    if (err) {
      setMsg(err.message);
      setIsError(true);
      return;
    }
    reload();
  }

  async function move(id: string, dir: -1 | 1) {
    const idx = items.findIndex((i) => i.id === id);
    const targetIdx = idx + dir;
    if (idx < 0 || targetIdx < 0 || targetIdx >= items.length) return;

    const reordered = [...items];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(targetIdx, 0, moved);

    for (let i = 0; i < reordered.length; i += 1) {
      const { error: err } = await supabase
        .from(table)
        .update({ sort_order: i })
        .eq("id", reordered[i].id);
      if (err) {
        setMsg(err.message);
        setIsError(true);
        return;
      }
    }
    reload();
  }

  function renderField(field: CrudField) {
    const value = form[field.key];
    const isRequired = field.required ?? false;

    if (field.type === "textarea") {
      return (
        <textarea
          rows={3}
          required={isRequired}
          value={(value as string) ?? ""}
          onChange={(e) => updateField(field.key, e.target.value)}
          placeholder={field.placeholder}
          className={inputClass}
        />
      );
    }

    if (field.type === "icon") {
      return (
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
            <i className={`${(value as string) || "ri-heart-3-line"} text-lg`}></i>
          </span>
          <select
            value={(value as string) ?? "ri-heart-3-line"}
            onChange={(e) => updateField(field.key, e.target.value)}
            className={inputClass}
          >
            {iconOptions.map((icon) => (
              <option key={icon} value={icon}>
                {icon.replace("ri-", "").replace("-line", "")}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === "image") {
      return (
        <ImageUpload
          value={(value as string) ?? ""}
          onChange={(url) => updateField(field.key, url)}
          required={isRequired}
        />
      );
    }

    return (
      <input
        type={field.type === "number" ? "number" : "text"}
        required={isRequired}
        value={(value as string | number) ?? ""}
        onChange={(e) =>
          updateField(
            field.key,
            field.type === "number"
              ? Number(e.target.value) || 0
              : e.target.value
          )
        }
        placeholder={field.placeholder}
        className={inputClass}
      />
    );
  }

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            {title}
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600">
            {description}
          </p>
        </div>
        <button
          type="button"
          onClick={startAdd}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer"
        >
          <i className="ri-add-line"></i>
          {addLabel}
        </button>
      </div>

      {(msg || error) && (
        <div
          className={`mt-6 rounded-md px-4 py-3 text-sm ${
            isError ? "bg-accent-100 text-accent-700" : "bg-primary-100 text-primary-700"
          }`}
        >
          {isError ? msg : error || msg}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleSave}
          className="mt-6 bg-background-50 rounded-lg border border-background-200 p-6 md:p-8 space-y-4"
        >
          <h3 className="font-heading text-base font-semibold text-foreground-950">
            {editingId ? "Modifier l'élément" : "Nouvel élément"}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map((field) => (
              <div
                key={field.key}
                className={
                  field.type === "textarea" || field.type === "image"
                    ? "sm:col-span-2"
                    : ""
                }
              >
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  {field.label}
                </label>
                {renderField(field)}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enregistrement…
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 px-6 py-2.5 text-sm font-semibold transition cursor-pointer"
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : (
        <div className="mt-8 bg-background-50 rounded-lg border border-background-200 divide-y divide-background-200">
          {items.length === 0 ? (
            <p className="p-8 text-sm text-foreground-500">
              Aucun élément pour le moment. Cliquez sur « {addLabel} ».
            </p>
          ) : (
            items.map((item, idx) => (
              <div key={item.id} className="flex items-center gap-4 p-4 md:p-5">
                {listImageKey && item[listImageKey] ? (
                  <div className="w-14 h-14 shrink-0 rounded-md overflow-hidden border border-background-200">
                    <img
                      src={item[listImageKey] as string}
                      alt={(item[listTitleKey] as string) ?? ""}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                ) : listIconKey && item[listIconKey] ? (
                  <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${item[listIconKey]} text-lg`}></i>
                  </span>
                ) : null}

                <div className="flex-1 min-w-0">
                  <p className="font-heading text-base font-bold text-foreground-950 truncate">
                    {item[listTitleKey] as string}
                  </p>
                  {listSubtitleKey && item[listSubtitleKey] ? (
                    <p className="text-sm text-foreground-600 truncate">
                      {item[listSubtitleKey] as string}
                    </p>
                  ) : null}
                </div>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => move(item.id, -1)}
                    disabled={idx === 0}
                    aria-label="Monter"
                    className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-up-line"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => move(item.id, 1)}
                    disabled={idx === items.length - 1}
                    aria-label="Descendre"
                    className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-down-line"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(item)}
                    aria-label="Modifier"
                    className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition cursor-pointer"
                  >
                    <i className="ri-pencil-line"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    aria-label="Supprimer"
                    className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}